import { Button, ButtonColors } from '@chiroup/components/Button';
import { InputMasked } from '@chiroup/components/form/InputMasked';
import { Loading } from '@chiroup/components/Loading';
import { ChiroUpLoginCommon } from '@chiroup/core/constants/stringConstants';
import { EmailValidator } from '@chiroup/hooks/validation/emailValidator';
import { Mail as EnvelopeIcon, Phone as PhoneIcon, User as UserIcon } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { portalSession } from '../services/portalSession';

// Custom auth replaces Cognito. Local stand-in matching Container's
// `AuthUser` shape so `setUser` is type-compatible without a cast.
type AuthUser = { userId: string; username: string };

const isValidPhone = (phone: string) => {
  return /^[0-9]{10,}$/.test(phone);
};

const loginSubmitOkay = (inputType: string, u: string, dob: string): boolean => {
  const isValidInput = inputType === 'phone' ? isValidPhone(u) : EmailValidator.value.test(u);

  return isValidInput && (dob || '').length === 10;
};

export const SubmitButtonClassName =
  'mt-4 focus:ring-2 focus:ring-primary focus:border-white focus:border-primary';

const detectInputType = (input: string) => {
  if (isValidPhone(input)) {
    return 'phone';
  }

  if (input.includes('@')) {
    return 'email';
  }

  return 'username';
};

type Props = {
  setUser: React.Dispatch<React.SetStateAction<AuthUser | null>>;
  path: string;
  getMe: (force?: boolean) => Promise<void>;
  values?: any;
  setValues?: (val: any) => void;
  onChange?: (key: string) => (value: any) => void;
  me?: {
    patients: any[];
    country?: string;
  } | null;
  clinicLocationData: any;
  /**
   * Called after successful sign-in (setUser + getMe have already
   * resolved). When provided, it replaces the default
   * `navigate(\`/${path}\`)` redirect — this is what the embedded
   * public-booking flow uses so that authenticating doesn't tear down
   * the in-progress appointment selection. When omitted, the legacy
   * standalone-page behaviour is preserved.
   */
  onAuthSuccess?: () => void;
};

type UiParts = {
  alert: {
    isVisible: boolean;
    message: string;
  };
  triesExhausted: boolean;
};

const uiPartsDefault: UiParts = {
  alert: {
    isVisible: false,
    message: '',
  },
  triesExhausted: false,
};

const Login: React.FC<Props> = ({
  setUser,
  path,
  getMe,
  values,
  onChange,
  setValues,
  me,
  clinicLocationData,
  onAuthSuccess,
}) => {
  const navigate = useNavigate();
  const [waiting, setWaiting] = useState<boolean>(false);
  // Surfaced when the backend tells us the start was throttled (the
  // rate-limit layer suppressed the actual SMS / email send). Lets us
  // swap the "code sent" copy for an honest "wait Xs" message.
  const [throttleWaitSeconds, setThrottleWaitSeconds] = useState(0);
  const [username, setUsername] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState<string>('');
  const [isSubmitting, setSubmitting] = useState(false);
  const [code, setCode] = useState<string>('');
  // Holds the OTP challenge id between the "send code" and the
  // "verify code" submits. Replaces the old Cognito session object.
  const [challengeId, setChallengeId] = useState<string>('');
  const isCountryUS = clinicLocationData?.country === 'US' || clinicLocationData?.country === 'USA';

  const inputType = isCountryUS ? detectInputType(username) : 'email';
  const [emailCellFocused, setEmailCellFocused] = useState<boolean>(false);
  const [uiParts, setUiParts] = useState<UiParts>(uiPartsDefault);

  const pairDevice = async (sendAgain?: boolean) => {
    if (!dateOfBirth) {
      return;
    }

    setSubmitting(true);
    setUiParts(p => ({ ...p, alert: { isVisible: false, message: '' } }));

    if (waiting && !sendAgain) {
      try {
        const verifyRes = await authService.verifyOtp({ challengeId, code });

        if (!verifyRes.matches?.length) {
          setUiParts(p => ({
            ...p,
            alert: { isVisible: true, message: ChiroUpLoginCommon.invalidCode },
          }));
          setSubmitting(false);

          return;
        }

        // Bind to the first match. The clinic-aware variant of the
        // patient picker lives in RootLanding; this inline /login
        // route is keyed on a single clinic `path`, so the first
        // match is the right one.
        const first = verifyRes.matches[0];

        await authService.startSession({
          challengeId,
          patientId: first.patientId,
          clinicId: first.clinicId,
        });
        setUser({ userId: first.patientId, username: first.patientId });

        if (portalSession.isLikelyValid()) {
          // force: true — bypass getMe's once-per-path gate, which a
          // pre-auth /me 401 may have already tripped.
          await getMe(true);
          setSubmitting(false);
          if (onAuthSuccess) {
            onAuthSuccess();
          } else {
            navigate(`/${path}`);
          }
        } else {
          setUiParts(p => ({
            ...p,
            alert: { isVisible: true, message: ChiroUpLoginCommon.invalidCode },
          }));
        }
      } catch (e: any) {
        if (!e || typeof e !== 'object') {
          setUiParts(p => ({
            ...p,
            alert: {
              isVisible: true,
              message: ChiroUpLoginCommon.connectionFailed,
            },
          }));
        } else if (e?.code === 'InvalidParameterException') {
          setUiParts(p => ({
            ...p,
            alert: {
              isVisible: true,
              message: ChiroUpLoginCommon.invalidCode,
            },
          }));
        } else if (e?.code === 'NotAuthorizedException') {
          setUiParts(p => ({
            ...p,
            triesExhausted: true,
            alert: {
              isVisible: true,
              message: ChiroUpLoginCommon.triesExhausted,
            },
          }));
        } else {
          setUiParts(p => ({
            ...p,
            alert: {
              isVisible: true,
              message: ChiroUpLoginCommon.connectionFailed,
            },
          }));
        }
      }

      setSubmitting(false);
    } else {
      try {
        const usernameToUse = inputType === 'phone' && isCountryUS ? `+1${username}` : username;
        const res = await authService.startOtp({
          contact: usernameToUse,
          contactType: inputType === 'phone' ? 'phone' : 'email',
          // Login surface: tell the backend to refuse delivery when no
          // Patient row matches, so the UI can surface an honest "no
          // account" message instead of pretending a code was sent.
          requireAccount: true,
        });

        if (res.hasMatchingAccount === false || !res.challengeId) {
          setUiParts(p => ({
            ...p,
            alert: {
              isVisible: true,
              message:
                res.message ||
                (inputType === 'phone'
                  ? "We couldn't find an account with that phone number."
                  : "We couldn't find an account with that email."),
            },
          }));
          setSubmitting(false);

          return;
        }

        if (res.smsOptedOut) {
          // No SMS went out — the phone texted STOP to our number and
          // the carrier blocks delivery. Stay on the identify step and
          // show the backend's opt-back-in copy ("text START to …")
          // instead of a code screen waiting for a code that won't come.
          setUiParts(p => ({
            ...p,
            alert: { isVisible: true, message: res.message },
          }));
          setSubmitting(false);

          return;
        }

        setChallengeId(res.challengeId);
        setThrottleWaitSeconds(res.throttled ? (res.retryAfterSeconds ?? 0) : 0);
        setSubmitting(false);
        setWaiting(true);
      } catch (err: any) {
        const errorMessage =
          err?.response?.data?.message || err?.message || ChiroUpLoginCommon.connectionFailed;
        setUiParts(p => ({
          ...p,
          alert: { isVisible: true, message: errorMessage },
        }));
        setSubmitting(false);
        onChange?.(inputType)(inputType === 'phone' ? `+1 ${username}` : username);
      }
    }
  };

  const showVanityPhone = !emailCellFocused && inputType === 'phone';

  return (
    <div className="flex flex-col gap-2 relative">
      <div
        className={`absolute left-0 right-0 grid place-items-center h-full ${
          isSubmitting ? 'z-20' : 'z-0'
        }`}
      >
        <Loading
          color="text-gray-400 dark:text-gray-500"
          className={isSubmitting ? '' : 'invisible'}
          percent={7}
        />
      </div>
      {waiting ? (
        <div className="flex flex-col gap-2 z-10">
          <div className="flex items-center gap-1">
            <span className="text-sm text-gray-900 dark:text-gray-100">
              {throttleWaitSeconds > 0
                ? `We didn't send another code — please wait about ${throttleWaitSeconds} seconds before requesting a new one. If you already received a code, you can enter it below.`
                : `Enter the validation code sent to your ${inputType}.`}
            </span>
            <div className="w-full border-t border-gray-300 dark:border-white/10 grow ml-2" />
          </div>
          <div className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-white/10 rounded-md p-4 flex flex-col gap-2 shadow-sm dark:shadow-none">
            <div>
              <label
                htmlFor="code"
                className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100"
              >
                Validation code
              </label>
              <input
                type="text"
                name="code"
                id="code"
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 ring-1 ring-inset ring-gray-300 dark:ring-white/10 placeholder:text-gray-400 dark:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                placeholder="123456"
                value={code}
                onChange={e => setCode(e.target.value)}
                disabled={uiParts.triesExhausted}
              />
              <div className="text-xs">
                Didn't receive a code? Check your spam folder or{' '}
                <span
                  className="text-primary cursor-pointer hover:underline"
                  onClick={() => {
                    setUiParts(uiPartsDefault);
                    setCode('');
                    pairDevice(true);
                  }}
                >
                  send a new code
                </span>
                .
              </div>
            </div>
            {!uiParts.triesExhausted && (
              <Button
                onClick={() => pairDevice()}
                disabled={isSubmitting || !(code.length > 5)}
                text="Submit"
                color={ButtonColors.plainWithBorder}
                className={SubmitButtonClassName}
              />
            )}
            {uiParts.triesExhausted && (
              <Button
                autoFocus
                onClick={() => {
                  setWaiting(false);
                  setUiParts(uiPartsDefault);
                  setCode('');
                }}
                text="Start Over"
                color={ButtonColors.plainWithBorder}
                className={SubmitButtonClassName}
              />
            )}{' '}
          </div>
          {uiParts.alert.isVisible && (
            <div
              className="bg-red-100 dark:bg-red-500/10 border border-red-400 dark:border-red-500/30 text-red-700 dark:text-red-300 px-4 py-3 rounded relative"
              role="alert"
            >
              {/* <strong className="font-bold">Holy smokes!</strong> */}
              <span className="block sm:inline">
                <span className="block w-5/6">{uiParts.alert.message}</span>
              </span>
              <span
                className="absolute top-0 bottom-0 right-0 px-4 py-3"
                onClick={() => {
                  setUiParts(p => ({
                    ...p,
                    alert: { isVisible: false, message: '' },
                  }));
                }}
              >
                <svg
                  className="fill-current h-6 w-6 text-red-500"
                  role="button"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <title>Close</title>
                  <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
                </svg>
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <span className="text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
              Enter the details below to login or sign up!
            </span>
            <div className="w-full border-t border-gray-300 dark:border-white/10 grow ml-2" />
          </div>
          <div className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-white/10 rounded-md p-4 flex flex-col gap-2 shadow-sm dark:shadow-none">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-900 dark:text-gray-100"
              >
                {isCountryUS ? 'Email or Mobile Number' : 'Email Address'}
              </label>

              <div className="relative rounded-md shadow-sm flex items-center">
                {showVanityPhone && (
                  <span
                    className="text-sm absolute leading-6 bg-white pointer-events-none left-10 top-0 h-full w-[calc(100%-2.5rem)] flex items-center rounded-md"
                    onClick={() => setEmailCellFocused(true)}
                  >
                    <span>
                      {inputType === 'phone'
                        ? `${username.substring(0, 3)} ${username.substring(
                            3,
                            6
                          )} ${username.substring(6, 10)}`
                        : ''}
                    </span>
                  </span>
                )}
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  {inputType === 'email' ? (
                    <EnvelopeIcon
                      className="h-5 w-5 text-gray-400 dark:text-gray-500"
                      aria-hidden="true"
                    />
                  ) : inputType === 'phone' && isCountryUS ? (
                    <PhoneIcon
                      className="h-5 w-5 text-gray-400 dark:text-gray-500"
                      aria-hidden="true"
                    />
                  ) : (
                    <UserIcon
                      className="h-5 w-5 text-gray-400 dark:text-gray-500"
                      aria-hidden="true"
                    />
                  )}
                </div>
                <input
                  type="text"
                  name="username"
                  id="username"
                  className="block w-full rounded-md py-1.5 pl-10 text-gray-900 dark:text-gray-100 ring-1 ring-gray-300 dark:ring-white/10 placeholder:text-gray-400 dark:text-gray-500 focus:ring-2 focus:ring-primary border-white focus:border-primary text-sm leading-6"
                  placeholder={isCountryUS ? 'you@example.com or 1234567890' : 'you@example.com'}
                  value={username}
                  onFocus={() => setEmailCellFocused(true)}
                  onChange={e => setUsername(e.target.value)}
                  onBlur={e => {
                    setEmailCellFocused(false);

                    if (setValues) {
                      if (inputType === 'phone') {
                        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
                        setValues((p: any) => ({
                          ...p,
                          phone: `+1 ${e?.target?.value}`,
                        }));
                      } else if (inputType === 'email') {
                        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
                        setValues((p: any) => ({
                          ...p,
                          email: e.target.value,
                        }));
                      } else {
                        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
                        setValues((p: any) => ({
                          ...p,
                          username: e.target.value,
                        }));
                      }
                    }
                  }}
                />
              </div>
            </div>
            <div>
              <InputMasked
                label="Date of birth"
                name="dateOfBirth"
                value={dateOfBirth}
                onChange={e => {
                  setDateOfBirth(e);
                  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
                  setValues?.((p: any) => ({ ...p, dateOfBirth: e }));
                }}
                patternFormat="##/##/####"
                placeholder="MM/DD/YYYY"
              />
            </div>
            <Button
              onClick={() => pairDevice()}
              disabled={isSubmitting || !loginSubmitOkay(inputType, username, dateOfBirth)}
              text="Submit"
              color={ButtonColors.plainWithBorder}
              className={SubmitButtonClassName}
            />
          </div>
          {uiParts.alert.isVisible && (
            <div
              className="bg-red-100 dark:bg-red-500/10 border border-red-400 dark:border-red-500/30 text-red-700 dark:text-red-300 px-4 py-3 rounded relative"
              role="alert"
            >
              <span className="block sm:inline">
                <span className="block w-5/6">{uiParts.alert.message}</span>
              </span>
              <span
                className="absolute top-0 bottom-0 right-0 px-4 py-3"
                onClick={() => {
                  setUiParts(p => ({
                    ...p,
                    alert: { isVisible: false, message: '' },
                  }));
                }}
              >
                <svg
                  className="fill-current h-6 w-6 text-red-500"
                  role="button"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <title>Close</title>
                  <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
                </svg>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Login;
